import {
	type CommandConstructor,
	type CommandProperties,
	type SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

/**
 * Base properties for all streaming lifecycle commands.
 * Contains the stream identifier and content information used to track the lifecycle of a message stream.
 */
interface StreamingLifecycleCommandProperties extends CommandProperties {
	/**
	 * The unique identifier for the message stream.
	 *
	 * @desription
	 * This corresponds to the unique ID tied to 1 unique network stream.
	 */
	streamId: string;

	/**
	 * The content ID associated with this stream.
	 *
	 * @description
	 * This is used to determine the appropriate receiver client based on the content context.
	 * The receiver will be determined by looking up which content context (staging-area or global)
	 * this `id` belongs to in the Rovo chat store context.
	 *
	 * @description Marked as optional for cases where we may not have the content ID yet. E.g.,
	 * in some errors cases we may not have the content ID as of yet.
	 */
	contentId?: string | number;
}

/**
 * A command sent by the Rovo client to broadcast that a message stream has completed successfully.
 *
 * @description
 * This is fired when the stream processing loop completes without errors.
 *
 * @summary {@link CommandProperties.senderId} is notifying {@link CommandProperties.receiverId}
 * that the message stream with the given streamId has completed successfully.
 */
export const RovoStreamCompletedCommand: CommandConstructor<
	StreamingLifecycleCommandProperties,
	SerializableValue
> = createCommand<StreamingLifecycleCommandProperties>('rovo-stream-completed-command');

/**
 * A command sent by the Rovo client to broadcast that a message stream has encountered an error.
 *
 * @description
 * This is fired when the stream processing encounters an error, or hangs
 *
 * @summary {@link CommandProperties.senderId} is notifying {@link CommandProperties.receiverId}
 * that the message stream with the given streamId has encountered an error and failed.
 */
export const RovoStreamErroredCommand: CommandConstructor<
	StreamingLifecycleCommandProperties,
	SerializableValue
> = createCommand<StreamingLifecycleCommandProperties>('rovo-stream-errored-command');

/**
 * A command sent by the Rovo client to broadcast that a message stream has been aborted by the user.
 *
 * @description
 * This is fired when the user manually aborts the stream, or the abort controller in Rovo is triggered.
 *
 * @summary {@link CommandProperties.senderId} is notifying {@link CommandProperties.receiverId}
 * that the message stream with the given streamId has been aborted by user action.
 */
export const RovoStreamAbortedCommand: CommandConstructor<
	StreamingLifecycleCommandProperties,
	SerializableValue
> = createCommand<StreamingLifecycleCommandProperties>('rovo-stream-aborted-command');
